export type VoiceSessionIssuanceRetryDecision =
  | {
      action: "retry";
      startAttemptId: string | null;
    }
  | {
      action: "fail";
      reason: "server_shutdown_unconfirmed";
    };

export function decideVoiceSessionIssuanceRetry(input: {
  startAttemptId: string;
  hasConnectionDetails: boolean;
  serverShutdownSucceeded: boolean;
}): VoiceSessionIssuanceRetryDecision {
  if (!input.hasConnectionDetails) {
    return { action: "retry", startAttemptId: input.startAttemptId };
  }

  if (input.serverShutdownSucceeded) {
    return { action: "retry", startAttemptId: null };
  }

  return { action: "fail", reason: "server_shutdown_unconfirmed" };
}

const UNRESOLVED_ISSUANCE_CODES = new Set([
  "VOICE_SESSION_ISSUANCE_IN_PROGRESS",
  "START_ATTEMPT_EXPIRED",
]);

// These validations run before claiming an issuance lease. Unknown responses,
// conflicts and server failures cannot prove that an earlier issuance stopped.
const PRE_ISSUANCE_REJECTIONS: Readonly<Record<string, number>> = {
  INVALID_START_ATTEMPT_ID: 400,
  INVALID_REQUEST: 400,
  REQUEST_TOO_LARGE: 413,
  REQUEST_FIELD_TOO_LARGE: 413,
  INVALID_REQUEST_ORIGIN: 403,
  MATHS_CONVERSATION_INVALID: 403,
  MATHS_CONTINUATION_REJECTED: 400,
};

export function shouldRetainVoiceSessionIssuance(input: {
  status: number;
  payload: unknown;
  startAttemptId?: string;
}): boolean {
  if (isVoiceSessionReplacementSafe(input)) return false;
  const code =
    input.payload && typeof input.payload === "object"
      ? (input.payload as { code?: unknown }).code
      : undefined;
  return typeof code !== "string" || PRE_ISSUANCE_REJECTIONS[code] !== input.status;
}

// This contract is emitted only after a pre-dispatch failure has settled and
// its completed lease body has been persisted. An error code alone is no proof.
export function isVoiceSessionReplacementSafe(input: {
  status: number;
  payload: unknown;
  startAttemptId?: string;
}): boolean {
  if (
    input.status !== 503 ||
    !input.startAttemptId ||
    !input.payload ||
    typeof input.payload !== "object"
  )
    return false;
  const body = input.payload as Record<string, unknown>;
  return (
    body.code === "LOCAL_VOICE_AGENT_UNAVAILABLE" &&
    body.replacementSafe === true &&
    body.startAttemptId === input.startAttemptId
  );
}

const DEFAULT_ISSUANCE_RETRY_AFTER_MS = 1000;
const MIN_ISSUANCE_RETRY_AFTER_MS = 250;
const MAX_ISSUANCE_RETRY_AFTER_MS = 2000;

export function parseVoiceSessionIssuanceInProgressRetry(input: {
  status: number;
  payload: unknown;
  retryAfterHeader: string | null;
}): { retryAfterMs: number } | null {
  if (input.status !== 409 || !input.payload || typeof input.payload !== "object") return null;

  const code = (input.payload as { code?: unknown }).code;
  if (typeof code !== "string" || !UNRESOLVED_ISSUANCE_CODES.has(code)) return null;

  const retryAfterSeconds = Number(input.retryAfterHeader);
  const requestedDelayMs =
    Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
      ? retryAfterSeconds * 1000
      : DEFAULT_ISSUANCE_RETRY_AFTER_MS;

  return {
    retryAfterMs: Math.min(
      MAX_ISSUANCE_RETRY_AFTER_MS,
      Math.max(MIN_ISSUANCE_RETRY_AFTER_MS, requestedDelayMs)
    ),
  };
}
